import { lazy, useEffect, type ReactNode } from "react";
import {
  createBrowserRouter,
  Navigate,
  RouterProvider,
  useLocation,
  useRouteError,
} from "react-router";
import { useTranslation } from "react-i18next";
import Layout from "./components/Layout";
import { report } from "./core/telemetry";
import { useStore } from "./core/store";
const entry = () => import("./features/Entry");
const auth = () => import("./features/Auth");
const profile = () => import("./features/Profile");
const Start = lazy(() => entry().then((m) => ({ default: m.Start }))),
  Instructions = lazy(() => entry().then((m) => ({ default: m.Instructions }))),
  NotFound = lazy(() => entry().then((m) => ({ default: m.NotFound })));
const Login = lazy(() => auth().then((m) => ({ default: m.Login }))),
  Phone = lazy(() => auth().then((m) => ({ default: m.PhoneAuthentication }))),
  Code = lazy(() => auth().then((m) => ({ default: m.CodeAuthentication })));
const Profile = lazy(() => profile().then((m) => ({ default: m.Profile }))),
  Form = lazy(() => profile().then((m) => ({ default: m.ProfileForm }))),
  Avatar = lazy(() => profile().then((m) => ({ default: m.Avatar })));
const Scanner = lazy(() =>
    import("./features/Scanner").then((m) => ({ default: m.Scanner })),
  ),
  TypeCode = lazy(() =>
    import("./features/Scanner").then((m) => ({ default: m.TypeCode })),
  ),
  Home = lazy(() => import("./features/Home")),
  Feedback = lazy(() => import("./features/Feedback"));
function Protected({ children }: { children: ReactNode }) {
  const logged = useStore((s) => !!s.credential?.access_token);
  const location = useLocation();
  return logged ? (
    children
  ) : (
    <Navigate
      replace
      to={`/login?returnTo=${encodeURIComponent(location.pathname + location.search)}`}
    />
  );
}
function RouteError() {
  const error = useRouteError();
  useEffect(() => report(error), [error]);
  const { t } = useTranslation();
  return (
    <div className="app-shell">
      <h1>{t("ui.generic")}</h1>
      <a className="button" href="/start">
        {t("ui.returnStart")}
      </a>
    </div>
  );
}
const router = createBrowserRouter([
  {
    element: <Layout />,
    errorElement: <RouteError />,
    children: [
      { index: true, element: <Navigate to="/start" replace /> },
      { path: "start", element: <Start /> },
      { path: "in/:accessKey", element: <Instructions /> },
      { path: "login", element: <Login /> },
      { path: "phone-authentication", element: <Phone /> },
      { path: "code-authentication", element: <Code /> },
      {
        path: "onboarding",
        element: (
          <Protected>
            <Form stage="basic" />
          </Protected>
        ),
      },
      {
        path: "onboarding/additional-data",
        element: (
          <Protected>
            <Form stage="additional" />
          </Protected>
        ),
      },
      {
        path: "onboarding/avatar-selection",
        element: (
          <Protected>
            <Avatar />
          </Protected>
        ),
      },
      {
        path: "profile",
        element: (
          <Protected>
            <Profile />
          </Protected>
        ),
      },
      {
        path: "profile/edit",
        element: (
          <Protected>
            <Form stage="edit" />
          </Protected>
        ),
      },
      { path: "qr-scanner", element: <Scanner /> },
      {
        path: "type-code",
        element: (
          <Protected>
            <TypeCode />
          </Protected>
        ),
      },
      { path: "checkout", element: <Navigate replace to="/home" /> },
      {
        path: "home",
        element: (
          <Protected>
            <Home />
          </Protected>
        ),
      },
      {
        path: "feedback",
        element: (
          <Protected>
            <Feedback />
          </Protected>
        ),
      },
      { path: "*", element: <NotFound /> },
    ],
  },
]);
export default function App() {
  return <RouterProvider router={router} />;
}
